import { type Data } from 'core/domain/Request'
import { type HttpClientConfig, type HttpClientInterface } from 'core/infra/http/HttpClient'

import { type Driver } from '../../domain/Driver'
import { type EmptyLocation } from '../../domain/shipment/EmptyLocation'
import { type Shipment } from '../../domain/shipment/Shipment'
import { mapApiToEmptyLocation, type RawEmptyLocation } from './emptyLocationMapper'
import { mapApiToShipment, type RawShipment } from './shipmentMapper'

const BASE_URL = '/api/v2/shipments'

type ShipmentsRepositoryDependencies = {
  httpClient: HttpClientInterface
}

export type TruckInfoPayload = {
  shipmentId: string
  tractorNumber?: string
  trailerNumber?: string
}

export type EmptyLocationPayload = {
  shipmentId: string
  availabilityDate: string
  city: string
  state: string
}

export class ShipmentsRepository {
  private httpClient: HttpClientInterface

  constructor({ httpClient }: ShipmentsRepositoryDependencies) {
    this.httpClient = httpClient
  }

  async assignDriver(
    shipmentId: string,
    driver: Driver,
    config?: Partial<HttpClientConfig>
  ): Promise<void> {
    const endpoint = `${BASE_URL}/${shipmentId}/assign_driver`
    await this.httpClient.post(endpoint, { driver_id: driver.id }, config)
  }

  async addEmptyLocation(
    { shipmentId, availabilityDate, city, state }: EmptyLocationPayload,
    config?: Partial<HttpClientConfig>
  ): Promise<EmptyLocation> {
    const endpoint = `${BASE_URL}/${shipmentId}/empty_location`
    const { data } = await this.httpClient.post<Data<RawEmptyLocation>>(
      endpoint,
      {
        availability_date: availabilityDate,
        city: city,
        state: state,
      },
      config
    )
    return mapApiToEmptyLocation(data.data)
  }

  async updateEmptyLocation(
    { shipmentId, availabilityDate, city, state }: EmptyLocationPayload,
    config?: Partial<HttpClientConfig>
  ): Promise<EmptyLocation> {
    const endpoint = `${BASE_URL}/${shipmentId}/empty_location`
    const { data } = await this.httpClient.put<Data<RawEmptyLocation>>(
      endpoint,
      {
        availability_date: availabilityDate,
        city: city,
        state: state,
      },
      config
    )
    return mapApiToEmptyLocation(data.data)
  }

  async getEmptyLocation(
    shipmentId: string,
    config?: Partial<HttpClientConfig>
  ): Promise<EmptyLocation> {
    const endpoint = `${BASE_URL}/${shipmentId}/empty_location`
    const { data } = await this.httpClient.get<Data<RawEmptyLocation>>(endpoint, config)
    return mapApiToEmptyLocation(data.data)
  }

  async setTruckInfo(
    { shipmentId, tractorNumber, trailerNumber }: TruckInfoPayload,
    config?: Partial<HttpClientConfig>
  ): Promise<Shipment> {
    const endpoint = `${BASE_URL}/${shipmentId}`
    const { data } = await this.httpClient.patch<RawShipment>(
      endpoint,
      {
        tractor_number: tractorNumber,
        trailer_number: trailerNumber,
      },
      config
    )
    return mapApiToShipment(data)
  }

  async getShipment(shipmentId: string, config?: Partial<HttpClientConfig>): Promise<Shipment> {
    const endpoint = `${BASE_URL}/${shipmentId}`
    const { data } = await this.httpClient.get<Data<RawShipment>>(endpoint, config)

    return mapApiToShipment(data.data)
  }
}
